import { Injectable, Signal, inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, forkJoin, map, of, tap } from 'rxjs';
import { environment } from '../../environments';
import { AuthTokenService } from './auth-token.service';
import { AuthService } from './auth.service';

// Always loaded as the base layer: the API echoes the key back for untranslated entries.
export const FALLBACK_LANGUAGE = 'en';

// Used when the login context carries no language.
export const DEFAULT_LANGUAGE = 'en'; //fr

export type TranslationBundle = Record<string, string>;

const LANGUAGE_STORAGE_KEY = 'rem_language';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly apiBaseUrl = environment.apiUrl;

  private readonly dictionary = signal<TranslationBundle>({});
  private readonly activeLanguage = signal<string>(DEFAULT_LANGUAGE);
  private readonly dictionaryVersion = signal(0);

  readonly language: Signal<string> = this.activeLanguage.asReadonly();
  readonly version: Signal<number> = this.dictionaryVersion.asReadonly();

  private loaded = false;
  private userSelectedLanguage = false;

  constructor(
    private readonly authService: AuthService,
    private readonly authTokenService: AuthTokenService
  ) {
    const storedLanguage = this.normalizeLanguage(this.readStoredLanguage());
    if (storedLanguage) {
      this.userSelectedLanguage = true;
      this.activeLanguage.set(storedLanguage);
    }
    this.applyDocumentLanguage(this.activeLanguage());

    // Replays the stored context synchronously, so the parameter is set before initialize() fetches.
    // An explicit user choice from the header always wins over the context language.
    this.authService.currentContext$.subscribe((context) => {
      if (!this.userSelectedLanguage) {
        this.setLanguage(context?.language);
      }
    });
    this.authTokenService.accessToken$.subscribe((token) => {
      if (token && !this.loaded) {
        this.load(this.activeLanguage()).subscribe();
      }
    });
  }

  /**
   * Sets the language parameter. Fed from the login context today, from the user profile later.
   * Before the initial load it only records the choice; afterwards it swaps the live bundle.
   */
  setLanguage(language: string | null | undefined): void {

    const normalizedLanguage = this.normalizeLanguage(language);
    if (!normalizedLanguage || (normalizedLanguage === this.activeLanguage() && this.loaded)) {
      return;
    }

    this.activeLanguage.set(normalizedLanguage);
    this.applyDocumentLanguage(normalizedLanguage);

    if (this.loaded || this.hasAnyAccessToken()) {
      this.load(normalizedLanguage).subscribe();
    }
  }

  /**
   * Applies a language chosen by the user and remembers it across reloads.
   */
  selectLanguage(language: 'en' | 'fr'): void {
    const normalizedLanguage = this.normalizeLanguage(language);
    if (!normalizedLanguage) {
      return;
    }

    this.userSelectedLanguage = true;
    this.storeLanguage(normalizedLanguage);
    this.setLanguage(normalizedLanguage);
  }

  /**
   * Loads the startup bundle. Called from APP_INITIALIZER so no view renders untranslated keys.
   */
  initialize(): Observable<void> {
    if (!this.hasAnyAccessToken()) {
      this.dictionary.set({});
      return of(undefined);
    }

    return this.load(this.activeLanguage());
  }

  /**
   * Returns true when either supported auth flow can attach a bearer token to API requests.
   */
  private hasAnyAccessToken(): boolean {
    return this.authService.isAuthenticated() || this.authTokenService.isAuthenticated();
  }

  private load(language: string): Observable<void> {
    const bundle$ =
      language === FALLBACK_LANGUAGE
        ? this.fetch(FALLBACK_LANGUAGE)
        : forkJoin({
            base: this.fetch(FALLBACK_LANGUAGE),
            target: this.fetch(language)
          }).pipe(map(({ base, target }) => this.merge(base, target)));

    return bundle$.pipe(
      tap((bundle) => {
        this.dictionary.set(bundle);
        this.dictionaryVersion.update((version) => version + 1);
        this.activeLanguage.set(language);
        this.applyDocumentLanguage(language);
        this.loaded = true;
      }),
      map(() => undefined),
      catchError(() => {
        console.warn(`[TranslationService] failed to load '${language}' bundle`);
        this.loaded = false;
        return of(undefined);
      })
    );
  }

  /**
  * Resolves a translation key, substituting any {{placeholder}} or #placeholder# the translated value declares.
   * Falls back to the key itself so a missing entry is visible rather than blank.
   */
  t(key: string, params?: Record<string, string | number>): string {
    const translated = this.dictionary()[key];
    const value = translated ?? key;
    return params ? this.interpolate(value, params) : value;
  }

  private fetch(language: string): Observable<TranslationBundle> {
    return this.http
      .get<TranslationBundle>(`${this.apiBaseUrl}/Translations`, { params: { language } });
  }

  // Screen readers use <html lang> to pick the pronunciation (WCAG 3.1.1).
  private applyDocumentLanguage(language: string): void {
    this.document.documentElement.lang = language;
  }

  private readStoredLanguage(): string | null {
    try {
      return localStorage.getItem(LANGUAGE_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private storeLanguage(language: string): void {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch {
      // Storage can be unavailable (e.g. privacy mode); the choice still applies for this session.
    }
  }

  private normalizeLanguage(language: string | null | undefined): string | null {
    if (!language) {
      return null;
    }

    const code = language.trim().toLowerCase().split('-')[0];
    return code === 'fr' || code === 'en' ? code : null;
  }

  // The API echoes the key back for entries that have no translation yet; those must not mask English.
  private merge(base: TranslationBundle, target: TranslationBundle): TranslationBundle {
    const merged: TranslationBundle = { ...base };
    for (const [key, value] of Object.entries(target)) {
      if (value && value !== key) {
        merged[key] = value;
      }
    }
    return merged;
  }

  private interpolate(value: string, params: Record<string, string | number>): string {
    return value.replace(/\{\{\s*(\w+)\s*\}\}|#(\w+)#/g, (placeholder, braceName: string | undefined, hashName: string | undefined) => {
      const name = braceName ?? hashName;
      return name && Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : placeholder;
    });
  }
}
